/**
 * Badges, empty states, page headers, stat tiles and the confirm dialog.
 *
 * The small pieces that appear on every screen. Kept together so their visual
 * language stays consistent - a status badge means the same thing on the orders
 * list as on the fiscal queue.
 */

import { FiInbox, FiAlertTriangle } from 'react-icons/fi';
import Modal from './Modal';

/* --------------------------------- Badge ---------------------------------- */

const TONES = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    brand: 'bg-brand-100 text-brand-800'
};

export const Badge = ({ tone = 'slate', children, className = '', icon: Icon = null }) => (
    <span className={`badge ${TONES[tone] || TONES.slate} ${className}`}>
        {Icon && <Icon className="h-3 w-3" />}
        {children}
    </span>
);

/**
 * Status badge with the mapping used across the app.
 * One place, so PAID is green everywhere and REJECTED is red everywhere.
 */
const STATUS_TONES = {
    // orders
    DRAFT: 'slate', HELD: 'amber', SENT: 'blue', BILLED: 'purple',
    PAID: 'green', VOID: 'red', REFUNDED: 'red',
    // fiscal
    PENDING: 'amber', SENDING: 'blue', ACCEPTED: 'green', REJECTED: 'red',
    FAILED: 'red', OFFLINE: 'slate', CANCELLED: 'slate', NOT_REQUIRED: 'slate',
    PROCESSING: 'blue', DONE: 'green',
    // stock
    OK: 'green', LOW: 'amber', OUT_OF_STOCK: 'red',
    // tables
    FREE: 'green', OCCUPIED: 'blue', RESERVED: 'purple', CLEANING: 'amber'
};

export const StatusBadge = ({ status, label = null, className = '' }) => (
    <Badge tone={STATUS_TONES[status] || 'slate'} className={className}>
        {label || String(status || '').replace(/_/g, ' ').toLowerCase()}
    </Badge>
);

/* ------------------------------- EmptyState -------------------------------- */

export const EmptyState = ({
    icon: Icon = FiInbox,
    title = 'Nothing here yet',
    description = null,
    action = null,
    className = ''
}) => (
    <div className={`flex flex-col items-center justify-center gap-3 px-4 py-10 text-center sm:px-6 sm:py-14 ${className}`}>
        <div className="rounded-full bg-slate-100 p-4">
            <Icon className="h-7 w-7 text-slate-400" />
        </div>
        <div>
            <p className="font-medium text-slate-700">{title}</p>
            {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
        </div>
        {action}
    </div>
);

/* ------------------------------- PageHeader -------------------------------- */

export const PageHeader = ({ title, subtitle = null, actions = null, className = '' }) => (
    <div
        className={`flex flex-col gap-2 rounded-2xl p-2 sm:flex-row sm:flex-wrap sm:items-end
                    sm:justify-between sm:gap-3 sm:p-3 ${className}`}
        style={{ background: 'linear-gradient(135deg, #05650fff 0%, #158f21ff 100%)' }}
    >
        <div className="min-w-0">
            <h1 className="truncate text-lg font-black tracking-tight text-white sm:text-xl lg:text-2xl">
                {title}
            </h1>
            {/* The subtitle is context, not instruction. On a phone the two
                lines of it would cost a row of the list underneath. */}
            {subtitle && <p className="mt-0.5 hidden text-sm text-white/70 sm:block">{subtitle}</p>}
        </div>

        {/* Every action stretches to share the row on a phone, so two buttons
            are two halves rather than one wide and one narrow. */}
        {actions && (
            <div className="flex flex-wrap items-center gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
                {actions}
            </div>
        )}
    </div>
);

/* --------------------------------- StatCard -------------------------------- */

export const StatCard = ({
    label, value, sub = null, icon: Icon = null, tone = 'slate', onClick = null
}) => {
    const toneRing = {
        slate: 'text-slate-600 bg-slate-100',
        green: 'text-emerald-600 bg-emerald-50',
        amber: 'text-amber-600 bg-amber-50',
        red: 'text-red-600 bg-red-50',
        blue: 'text-blue-600 bg-blue-50',
        brand: 'text-brand-600 bg-brand-50'
    }[tone] || 'text-slate-600 bg-slate-100';

    const Wrapper = onClick ? 'button' : 'div';

    return (
        <Wrapper
            onClick={onClick}
            className={`card flex w-full items-center gap-2.5 p-2 text-left sm:gap-4
                        ${onClick ? 'hover:border-brand-300' : ''}`}
        >
            {Icon && (
                <div className={`shrink-0 rounded-xl p-1 ${toneRing}`}>
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
            )}
            <div className="min-w-0">
                <p className="truncate text-[10px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs">
                    {label}
                </p>
                <p className="tabular mt-0.5 truncate text-base font-semibold text-slate-800 sm:text-xl">
                    {value}
                </p>
                {sub && <p className="mt-0.5 truncate text-[11px] text-slate-500 sm:text-xs">{sub}</p>}
            </div>
        </Wrapper>
    );
};

/* ------------------------------ ConfirmDialog ------------------------------ */

/**
 * Used for anything that cannot be undone - voiding an order, closing a shift,
 * deleting a menu item. `tone="danger"` makes the confirm button red so the
 * weight of the action is visible before the tap, not after.
 */
export const ConfirmDialog = ({
    open,
    title = 'Are you sure?',
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'danger',
    busy = false,
    onConfirm,
    onCancel
}) => (
    <Modal
        open={open}
        onClose={onCancel}
        title={title}
        width="sm"
        dismissible={!busy}
        footer={(
            <>
                <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
                    {cancelLabel}
                </button>
                <button
                    type="button"
                    className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
                    onClick={onConfirm}
                    disabled={busy}
                >
                    {busy ? 'Working…' : confirmLabel}
                </button>
            </>
        )}
    >
        <div className="flex gap-3">
            {tone === 'danger' && (
                <div className="shrink-0 rounded-full bg-red-50 p-2.5">
                    <FiAlertTriangle className="h-5 w-5 text-red-600" />
                </div>
            )}
            <p className="text-sm text-slate-600">{message}</p>
        </div>
    </Modal>
);

export default Badge;
